/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getParamsFromPath} from './utils'
import {absoluteUrl, removeSiteLocaleFromPath} from './url'
import {
    HYBRID_CONTENT,
    HYBRID_CUSTOMER_SERVICE,
    HYBRID_PDP,
    HYBRID_PLP,
    MY_STOKKE_CONFIG_LABELS,
    HYBRID_PRODUCT_EXCLUDE,
    HYBRID_SEARCH,
    MY_STOKKE_CONFIG_LABELS_D2B
} from '../constants'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'

/**
 * This functions takes message id and return locale specific translation.
 * @param {string} id is the message identifier
 * @param {string} type is label type
 * @returns {string} locale specific label
 */
export const getMyStokkeLabels = (id, type, formatMessage, isD2BEnabled) => {
    const configData = !isD2BEnabled ? MY_STOKKE_CONFIG_LABELS : MY_STOKKE_CONFIG_LABELS_D2B
    const label = configData?.[id]?.[type] ? formatMessage(configData[id][type]) : undefined
    return label?.startsWith('mystokke.') ? undefined : label
}

/**
 * This functions takes an url and returns a site object,
 * an error will be thrown if no url is passed in or no site is found
 * @param {string} url
 * @returns {object} site - a site object
 */
export const resolveSiteFromUrl = (url) => {
    if (!url) {
        throw new Error('URL is required to find a site object.')
    }
    const {pathname, search} = new URL(absoluteUrl(url))
    const path = `${pathname}${search}`
    let site

    // get the site identifier from the url
    const {siteRef} = getParamsFromPath(path)
    const sites = getSites()

    // step 1: use the siteRef to look for the site from the sites in the app config
    // since alias is optional, make sure it is defined before the equality check
    site = sites.find((site) => site.id === siteRef || (site.alias && site.alias === siteRef))
    if (site) {
        return site
    }

    //Step 2: if step 1 does not work, use the defaultSite value to get the default site
    site = getDefaultSite()
    // Step 3: throw an error if site can't be found by any of the above steps
    if (!site) {
        throw new Error(
            "Can't find a matching default site. Please check your sites configuration."
        )
    }
    return site
}

/**
 * Returns the default site based on the defaultSite value from the app config
 * @returns {object} site - a site object from app config
 */
export const getDefaultSite = () => {
    const {app} = getConfig()
    const sites = getSites()

    if (sites.length === 1) {
        return sites[0]
    }

    return sites.find((site) => site.id === app.defaultSite)
}

/**
 * Return the list of sites that has included their respective aliases
 * @return {array} sites - list of sites including their aliases
 */
export const getSites = () => {
    const {sites = [], siteAliases = {}} = getConfig().app || {}

    if (!sites.length) {
        throw new Error("Can't find any sites from the config. Please check your configuration")
    }

    return sites.map((site) => {
        const alias = siteAliases[site.id]
        return {
            ...site,
            ...(alias ? {alias} : {})
        }
    })
}

export const getContinents = () => {
    const {app} = getConfig()
    if (app.continents)
        return Object.entries(app.continents).map(([continentName, locales]) => ({
            continentName,
            locales
        }))
}

/**
 * Given a site reference, return the site object
 * @param siteRef - site reference to look for the site object
 * @returns {object | undefined} found site object or default site object
 */
export const getSiteByReference = (siteRef) => {
    const defaultSite = getDefaultSite()
    const sites = getSites()

    return (
        sites.find((site) => {
            return site.alias === siteRef || site.id === siteRef
        }) || defaultSite
    )
}

/**
 * Resolves if particular component requires browser navigation or not.
 *
 * Fallback to hybrid mode in case of broken configuration.
 *
 * @param {Object} hybridConfig - hybrid config object
 * @param {String} page - page type
 * @returns {boolean} - true if the page is hybrid, false otherwise
 */
export const isHybrid = (hybridConfig, page) => {
    if (!hybridConfig) {
        if (process.env.NODE_ENV !== 'test') {
            console.warn('Hybrid config is not defined. Assuming hybrid mode for all links.')
        }
        return true
    }
    if (!page) {
        throw new Error('Page is required to check if it is hybrid or not')
    }
    if (hybridConfig.enabled === false) {
        return false
    }
    if (!(page in hybridConfig)) {
        if (process.env.NODE_ENV !== 'test') {
            console.warn(`Missing hybrid config for page: ${page}. Assuming hybrid mode.`)
        }
        return true
    }
    return hybridConfig[page] !== false
}

/**
 * Guess if the current page is hybrid or not.
 * @param {Object} hybridConfig - hybrid config object
 * @param {String|URL} url - url to check
 * @returns {boolean} - true if the page is hybrid, false otherwise
 */
export const guessHybrid = (hybridConfig, url) => {
    url = new URL(url, getAppOrigin())

    // different origin - hybrid
    if (typeof window !== 'undefined' && url.origin !== window.location.origin) {
        return true
    }

    // global links are always hybrid
    if (/^\/global\/.*/i.test(url.pathname)) {
        return true
    }

    if (/^\/.*\/category\/.*/i.test(url.pathname)) {
        return isHybrid(hybridConfig, HYBRID_PLP)
    } else if (/^\/.*\/search.*/i.test(url.pathname)) {
        return isHybrid(hybridConfig, HYBRID_SEARCH)
    } else if (/^\/.*\/.*html/i.test(url.pathname)) {
        const pathWithoutSiteLocale = removeSiteLocaleFromPath(url.pathname)
        if (pathWithoutSiteLocale.startsWith('/ct/')) {
            return isHybrid(hybridConfig, HYBRID_CONTENT)
        } else if (pathWithoutSiteLocale.startsWith('/customer-service/')) {
            return isHybrid(hybridConfig, HYBRID_CUSTOMER_SERVICE)
        } else if (pathWithoutSiteLocale.startsWith('/newsroom/')) {
            return isHybrid(hybridConfig, HYBRID_CONTENT)
        } else if (pathWithoutSiteLocale.startsWith('/stokke/')) {
            return isHybrid(hybridConfig, HYBRID_CONTENT)
        } else if (pathWithoutSiteLocale.includes('/stokke-sleepi-dresser-changer.html')) {
            return isHybrid(hybridConfig, HYBRID_PRODUCT_EXCLUDE)
        } else if (pathWithoutSiteLocale.includes('/stokke-sleepi-dresser.html')) {
            return isHybrid(hybridConfig, HYBRID_PRODUCT_EXCLUDE)
        } else {
            return isHybrid(hybridConfig, HYBRID_PDP)
        }
    } else {
        return hybridConfig.enabled
    }
}
